import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { MessageCircle, CheckCircle, Copy, ExternalLink, Settings, Send, RefreshCw } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

export const TelegramConnection = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [isGeneratingLink, setIsGeneratingLink] = useState(false);
  const [generatedLink, setGeneratedLink] = useState<string | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [isConfiguringWebhook, setIsConfiguringWebhook] = useState(false);
  const [isSendingTest, setIsSendingTest] = useState(false);

  const { data: telegramLink, refetch } = useQuery({
    queryKey: ['telegram-connection', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;

      const { data } = await supabase
        .from('telegram_links')
        .select('*')
        .eq('user_id', user.id)
        .eq('status', 'linked')
        .order('linked_at', { ascending: false })
        .limit(1)
        .single();

      return data;
    },
    enabled: !!user?.id,
  });

  const { data: telegramUser } = useQuery({
    queryKey: ['telegram-user-settings', telegramLink?.chat_id],
    queryFn: async () => {
      if (!telegramLink?.chat_id) return null;

      const { data } = await supabase
        .from('telegram_users')
        .select('alerts_enabled')
        .eq('chat_id', telegramLink.chat_id)
        .single();

      return data;
    },
    enabled: !!telegramLink?.chat_id,
  });

  const { data: webhookStatus, refetch: refetchWebhook } = useQuery({
    queryKey: ['telegram-webhook-status'],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('telegram-admin/get-webhook');
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
    refetchInterval: false,
  });

  const { data: botInfo } = useQuery({
    queryKey: ['telegram-bot-info'],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('telegram-admin/get-bot-info');
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
  });

  const generateTelegramLink = async () => {
    if (!user?.id) return;

    setIsGeneratingLink(true);
    try {
      const token = crypto.randomUUID();
      const expiresAt = new Date(Date.now() + 10 * 60 * 1000); // 10 minutes

      // Hash the token before storing (SHA-256)
      const encoder = new TextEncoder();
      const hashBuffer = await crypto.subtle.digest('SHA-256', encoder.encode(token));
      const hashedToken = Array.from(new Uint8Array(hashBuffer))
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');

      const { error } = await supabase
        .from('telegram_links')
        .insert({
          user_id: user.id,
          token: hashedToken,
          expires_at: expiresAt.toISOString(),
          status: 'pending'
        });

      if (error) throw error;

      // Get bot username from backend
      const { data: botInfo, error: botError } = await supabase.functions.invoke('telegram-admin/get-bot-info');
      
      if (botError) {
        console.error('Bot info error:', botError);
        throw new Error('Erro ao obter informações do bot do Telegram');
      }
      
      if ((botInfo as any)?.error) {
        console.error('Bot info response error:', (botInfo as any).error);
        throw new Error((botInfo as any).error);
      }
      
      const botUsername = (botInfo as any)?.bot?.username || (botInfo as any)?.username || 'CristosCryptoBot';
      const telegramLink = `https://t.me/${botUsername}?start=${token}`;
      setGeneratedLink(telegramLink);

      // Start polling for connection
      setIsPolling(true);

      toast({
        title: "Link gerado!",
        description: "Clique no link para conectar sua conta no Telegram.",
      });
    } catch (error) {
      console.error('Error generating telegram link:', error);
      toast({
        title: "Erro",
        description: "Erro ao gerar link de conexão.",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingLink(false);
    }
  };

  // Polling effect to check for connection after generating link
  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (isPolling && generatedLink && !telegramLink) {
      interval = setInterval(async () => {
        await refetch();
      }, 3000); // Check every 3 seconds

      // Stop polling after 60 seconds
      setTimeout(() => {
        setIsPolling(false);
      }, 60000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isPolling, generatedLink, telegramLink, refetch]);

  // Stop polling when connection is established
  useEffect(() => {
    if (telegramLink && isPolling) {
      setIsPolling(false);
      setGeneratedLink(null);
      toast({
        title: "Conectado!",
        description: "Conta do Telegram conectada com sucesso.",
      });
    }
  }, [telegramLink, isPolling, toast]);

  const copyLink = () => {
    if (generatedLink) {
      navigator.clipboard.writeText(generatedLink);
      toast({
        title: "Copiado!",
        description: "Link copiado para a área de transferência.",
      });
    }
  };

  const configureWebhook = async () => {
    setIsConfiguringWebhook(true);
    try {
      const { data, error } = await supabase.functions.invoke('telegram-admin/set-webhook');
      
      if (error) throw error;

      await refetchWebhook();
      toast({
        title: "Webhook configurado!",
        description: "Bot do Telegram configurado com sucesso.",
      });
    } catch (error) {
      console.error('Error configuring webhook:', error);
      toast({
        title: "Erro",
        description: "Erro ao configurar webhook do Telegram.",
        variant: "destructive",
      });
    } finally {
      setIsConfiguringWebhook(false);
    }
  };

  const sendTestMessage = async () => {
    if (!telegramLink?.chat_id) {
      toast({ title: 'Telegram não conectado', description: 'Conecte seu Telegram antes de enviar um teste.', variant: 'destructive' });
      return;
    }

    setIsSendingTest(true);
    try {
      const { data, error } = await supabase.functions.invoke('telegram-test-report', {
        body: { reportType: 'test', testMode: true, skipDataCollection: true }
      });
      
      if (error) {
        throw new Error(error.message || 'Falha ao enviar teste');
      }

      if ((data as any)?.error) {
        throw new Error((data as any).error || 'Falha ao enviar teste');
      }

      toast({
        title: 'Mensagem enviada!',
        description: 'Mensagem de teste enviada para seu Telegram.',
      });
    } catch (err: any) {
      console.error('Error sending test message:', err);
      // Tenta extrair a mensagem detalhada do Edge Function
      const ctx = err?.context || {};
      const code = ctx?.data?.code || ctx?.error?.code;
      const serverMsg = ctx?.data?.error || ctx?.error || err?.message;

      let description = serverMsg || 'Erro ao enviar mensagem de teste.';
      if (code === 'TELEGRAM_NOT_LINKED') description = 'Telegram não conectado. Abra o bot e toque em INICIAR pelo link gerado.';
      if (code === 'BOT_TOKEN_MISSING') description = 'Configuração ausente: TELEGRAM_BOT_TOKEN nas Secrets das Functions.';
      if (code === 'TELEGRAM_API_ERROR') description = serverMsg?.replace('Erro no Telegram: ', '') || description;

      toast({
        title: 'Erro',
        description,
        variant: 'destructive',
      });
    } finally {
      setIsSendingTest(false);
    }
  };

  const toggleAlerts = async (enabled: boolean) => {
    if (!telegramLink?.chat_id) return;

    try {
      // Ensure row exists and update/create it
      const { error: upsertError } = await supabase
        .from('telegram_users')
        .upsert(
          { chat_id: telegramLink.chat_id, alerts_enabled: enabled, user_id: user?.id },
          { onConflict: 'chat_id' }
        );

      if (upsertError) throw upsertError;

      // Simple refresh to reflect state consistently
      window.location.reload();

      toast({
        title: enabled ? 'Alertas ativados!' : 'Alertas desativados!',
        description: enabled ? 'Você receberá notificações do mercado.' : 'Notificações pausadas.',
      });
    } catch (error: any) {
      console.error('Error toggling alerts:', error);
      toast({
        title: 'Erro',
        description: error?.message || 'Erro ao atualizar configuração de alertas.',
        variant: 'destructive',
      });
    }
  };

  const disconnectTelegram = async () => {
    if (!telegramLink) return;

    try {
      const { error } = await supabase
        .from('telegram_links')
        .update({ status: 'revoked' })
        .eq('id', telegramLink.id);

      if (error) throw error;

      await refetch();
      toast({
        title: "Desconectado!",
        description: "Conta do Telegram desconectada com sucesso.",
      });
    } catch (error) {
      console.error('Error disconnecting telegram:', error);
      toast({
        title: "Erro",
        description: "Erro ao desconectar conta do Telegram.",
        variant: "destructive",
      });
    }
  };

  if (!user) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-muted-foreground">Faça login para conectar ao Telegram.</p>
        </CardContent>
      </Card>
    );
  }

  const isWebhookConfigured = webhookStatus?.webhook?.url;
  
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MessageCircle className="h-5 w-5" />
          Configuração do Telegram
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Bot Info Section */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Bot Status</span>
            <div className="flex items-center gap-2">
              <Badge variant={isWebhookConfigured ? "default" : "secondary"}>
                {isWebhookConfigured ? "Webhook Ativo" : "Webhook Inativo"}
              </Badge>
            </div>
          </div>
          
          {botInfo?.bot && (
            <p className="text-sm text-muted-foreground">
              Bot: <span className="font-mono">@{botInfo.bot.username}</span>
            </p>
          )}

          {!isWebhookConfigured && (
            <Alert>
              <Settings className="h-4 w-4" />
              <AlertDescription>
                O webhook precisa ser configurado para que o bot responda às mensagens.
              </AlertDescription>
            </Alert>
          )}

          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={configureWebhook}
              disabled={isConfiguringWebhook}
            >
              <Settings className="h-4 w-4 mr-2" />
              {isConfiguringWebhook ? 'Configurando...' : 'Configurar Webhook'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetchWebhook()}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Verificar Status
            </Button>
          </div>
        </div>

        <Separator />

        {/* Connection Section */}
        {telegramLink ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-500" />
              <Badge variant="default" className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100">
                Conectado
              </Badge>
            </div>
            
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                Chat ID: <span className="font-mono">{telegramLink.chat_id}</span>
              </p>
              {telegramLink.linked_at && (
                <p className="text-xs text-muted-foreground">
                  Conectado em: {new Date(telegramLink.linked_at).toLocaleString('pt-BR')}
                </p>
              )}
            </div>
            
            <Alert>
              <MessageCircle className="h-4 w-4" />
              <AlertDescription>
                Sua conta está conectada ao Telegram! Configure os alertas abaixo.
              </AlertDescription>
            </Alert>

            {/* Alerts Toggle */}
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <p className="text-sm font-medium">Alertas Automáticos</p>
                <p className="text-xs text-muted-foreground">
                  Receber notificações sobre o mercado
                </p>
              </div>
            <Switch
              checked={telegramUser?.alerts_enabled || false}
              onCheckedChange={toggleAlerts}
            />
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={sendTestMessage}
                disabled={isSendingTest}
              >
                <Send className="h-4 w-4 mr-2" />
                {isSendingTest ? 'Enviando...' : 'Enviar Teste'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={disconnectTelegram}
              >
                Desconectar
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                Conecte sua conta ao Telegram para receber alertas importantes
                sobre o mercado de criptomoedas em tempo real.
              </p>
              
              <div className="text-xs text-muted-foreground space-y-1">
                <p>• Alertas de volatilidade</p>
                <p>• Notificações de oportunidades</p>
                <p>• Resumos do mercado</p>
              </div>
            </div>

            {generatedLink ? (
              <div className="space-y-3">
                <Alert>
                  <MessageCircle className="h-4 w-4" />
                  <AlertDescription>
                    <strong>Passo 1:</strong> Clique no link abaixo para abrir o Telegram
                    <br />
                    <strong>Passo 2:</strong> Clique em "INICIAR" no bot
                    {isPolling && (
                      <>
                        <br />
                        <span className="text-blue-600 dark:text-blue-400">
                          ⏳ Aguardando conexão...
                        </span>
                      </>
                    )}
                  </AlertDescription>
                </Alert>

                <div className="flex gap-2">
                  <Button
                    onClick={() => window.open(generatedLink, '_blank')}
                    className="flex-1"
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Abrir no Telegram
                  </Button>
                  <Button variant="outline" onClick={copyLink}>
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>

                <p className="text-xs text-muted-foreground">
                  Link válido por 10 minutos. Esta página será atualizada automaticamente quando você conectar.
                </p>
              </div>
            ) : (
              <Button 
                onClick={generateTelegramLink}
                disabled={isGeneratingLink}
                className="w-full"
              >
                <MessageCircle className="h-4 w-4 mr-2" />
                {isGeneratingLink ? 'Gerando link...' : 'Conectar ao Telegram'}
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};